import React from "react";
import { openDB } from "idb";
import { v4 as uuid } from "uuid";
import { withNavigation } from "../Components/helpers/withNavigation";
import { Alert, Button, Label, Input, FormGroup, ButtonGroup } from "reactstrap";
import { TourProvider, useTour } from '@reactour/tour';
import { TourBridge, SkipHiddenTourSteps } from '../utils/TourHelpers.js';
import { convertHeicToPng, convertMovToMp4 } from '../utils/fileConverter';
import { DB_NAME, STORE_NAMES } from "../App";

async function initDB() {
  return openDB(DB_NAME, 2, {
    upgrade(db) {
      STORE_NAMES.forEach((storeName) => {
        if (!db.objectStoreNames.contains(storeName)) {
          db.createObjectStore(storeName, { keyPath: "id" });
        }
      });
    },
  });
}

function MediaCaptureWithTour(props){
  const steps= [
    {
      selector:'.function-bar',
      content:(
      <>
        <ul>
          <li>Help to show this tour again.</li>
        </ul>
      </>
      ),
    },
    {      
      selector: '.file-input-container',
      content:(
        <>
          <ul>
            <li>Use this input to upload existing images or videos from your device instead of capturing new media.</li>
            <li>On mobile devices, you can also use it to open the camera or gallery.</li>
          </ul>
        </>
      ),
    },
    {
      selector: '.instruct-control-bar',
      content:(
      <>
        <ul>
          <li>Click the Start Camera Preview button if live view has not been started.</li>
          <li>Click the Return button to return to the previous page</li>
        </ul>
      </>
      ),
    },{
      selector: '.select-device-controls',
      content:(
        <>
          <ul>
            <li>Use these dropdowns to select the camera and microphone devices on your system.</li>
          </ul>
        </>
      ) ,
    },{
      selector: '.video-preview',
      content:(
        <>
          <ul>
            <li>This area shows the live camera preview. You can use it to capture images or record videos depending on the step type.</li>
          </ul>
        </>
      ),
    },
    {
      selector: '.media-capture-controls',
      content:(
        <>
          <ul>
            <li>Depending on whether you are capturing images or videos, use the appropriate buttons here to capture an image or start/stop video recording.</li>
            <li>If your device supports it, you can also toggle the torch (flashlight) on or off while capturing media.</li>
          </ul>
        </>
      ),
    },
    {
      selector: '.current-media',
      content:(
        <>
          <ul>
            <li>This area shows the media you have captured or uploaded for this training step.</li>
            <li>You can delete any media you no longer want to use.</li>
          </ul>
        </>
      ),
    }
  ]
  
  // const HandleStepChange = (currentStepIndex, previousStepIndex) => {
  //   const { setCurrentStep } = useTour();
  //   const step = steps[currentStepIndex];
  //   const el = document.querySelector(step.selector);
    
  //   // Function to check if the element is hidden
  //   const isElementHidden = (element) => {
  //     const styles = window.getComputedStyle(element);
  //     return (
  //       element.offsetParent === null || // hidden by CSS (e.g., display: none)
  //       styles.visibility === 'hidden' || // visibility is hidden
  //       styles.opacity === '0' || // opacity is 0
  //       styles.display === 'none' // display is none
  //     );
  //   };

  //   if (!el || isElementHidden(el)) {
  //     // Skip to the next step if the element is not visible or hidden
  //     if (currentStepIndex + 1 < steps.length) {
  //       setCurrentStep(currentStepIndex + 1);
  //     }else{
  //       setCurrentStep(0);
  //     }
  //   }
  // };

  return (
    <div className={'training-container'}>
      <TourProvider 
        steps={steps} 
        padding={10} 
        showBadge={false}
        scrollSmooth 
        className="my-custom-tour" 
        onClickClose={() => {props.updateState({showTour: false});}}
        onClickMask={() => {props.updateState({showTour: false});}} 
        // onCurrentStepChange={HandleStepChange}
       >
        <SkipHiddenTourSteps />
        <TourBridge showTour={props.showTour} incognitoBrowser={props.incognitoBrowser} updateState={props.updateState}>
          <MediaCapture {...props} />
        </TourBridge>
      </TourProvider>
    </div>
  );
}

class MediaCapture extends React.Component {
  constructor(props) {
    super(props);

    const curMediaID =this.props.currentStep ?
      this.props.currentStep.StepType === 4
        ? this.props.currentStep.VideoFilepath
        : this.props.currentStep.ContentImagePath : null;

    this.state = {
      mediaList: [],
      recording: false,
      videoDevices: [],
      audioDevices: [],
      selectedVideo: null,
      selectedAudio: null,
      torchAvailable: false,
      torchOn: false,
      fileType: this.props.currentStep
        ? this.props.currentStep.StepType === 4
          ? "video"
          : "image"
        : "image",
      curMediaID: curMediaID,
      cameraStarted: false,
      osLabel: "Select or Capture Media",
    };

    this.videoRef = React.createRef();
    this.mediaRecorderRef = null;
    this.chunksRef = [];
    this.currentVideoTrackRef = null;
  }

  componentDidMount() {
    this.detectOS();
    this.loadMedia();
    this.loadDevices();
  }

  detectOS = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    let osLabel = "Select or Capture Media";
    let mobile = false;

    if (/android/i.test(userAgent)) {
      osLabel = "Open Camera or Gallery";
      mobile = true;
    } else if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      osLabel = "Open Camera or Gallery";
      mobile = true;
    } else if (/Win/.test(userAgent)) {
      osLabel = "Choose " + this.state.fileType.charAt(0).toUpperCase() + this.state.fileType.slice(1);
    } else if (/Mac/.test(userAgent)) {
      osLabel = "Choose " + this.state.fileType.charAt(0).toUpperCase() + this.state.fileType.slice(1);
    }

    this.setState({ osLabel, mobile });
  };

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.currentStep?.StepType !== this.props.currentStep?.StepType) {
      this.setState({
        fileType: this.props.currentStep.StepType === 4 ? "video" : "image",
      });
    }

    if(this.props.currentStep?.StepType === 2 && prevProps.currentStep?.ContentImagePath !== this.props.currentStep?.ContentImagePath) {
      this.setState({ curMediaID: this.props.currentStep?.ContentImagePath }, () => {
        this.loadMedia();
      });
    }else if(this.props.currentStep?.StepType === 3 && prevProps.currentStep?.ImageFilepath !== this.props.currentStep?.ImageFilepath) {
      this.setState({ curMediaID: this.props.currentStep?.ImageFilepath }, () => {
        this.loadMedia();
      });
    }else if(this.props.currentStep?.StepType === 4 && prevProps.currentStep?.VideoFilepath !== this.props.currentStep?.VideoFilepath) {
      this.setState({ curMediaID: this.props.currentStep?.VideoFilepath }, () => {
        this.loadMedia();
      });
    } 

    // only restart camera if user has already started it manually
    if (
      this.state.cameraStarted &&
      (prevState.selectedVideo !== this.state.selectedVideo ||
        prevState.selectedAudio !== this.state.selectedAudio)
    ) {
      this.startCamera();
    }
  }

  componentWillUnmount() {
    this.stopCamera();
    if (this.videoRef.current && this.videoRef.current.srcObject) {
      this.videoRef.current.srcObject.getTracks().forEach((track) => track.stop());
      this.videoRef.current.srcObject = null;
    }
    if (this.mediaRecorderRef && this.mediaRecorderRef.state !== "inactive") {
      this.mediaRecorderRef.stop();
    }
    this.currentVideoTrackRef = null;
    this.setState({ cameraStarted: false });
  }

  // IndexedDB helpers (unchanged)
  loadMedia = async () => {
    const db = await initDB();
    const allMedia = await db.getAll(STORE_NAMES[1]);
    if (!allMedia || allMedia.length === 0) {
      this.setState({ mediaList: [] });
      return;
    }
    const allTrainingMedia = allMedia.filter(
      (m) =>
        m.type.includes(this.state.fileType) &&
        m.trainings?.includes(this.props.currentTraining?.Id)
    );
    const curStepMedia =
      allTrainingMedia.length > 0
        ? allTrainingMedia.filter((m) => m.id === this.state.curMediaID)
        : [];
    console.log("loadMedia allMedia:", allTrainingMedia, " curStepMedia:", curStepMedia);
    this.setState({ mediaList: curStepMedia });
  };

  saveToDB = async (file, type) => {
    const db = await initDB();
    const mediaID = uuid();
    var stepToUpdate = this.props.currentStep;
    if (stepToUpdate) {
      switch (stepToUpdate.StepType) {
        case 2:
          stepToUpdate = { ...stepToUpdate, ContentImagePath: mediaID };
          break;
        case 3:
          stepToUpdate = { ...stepToUpdate, ImageFilepath: mediaID };
          break;
        case 4:
          stepToUpdate = { ...stepToUpdate, VideoFilepath: mediaID };
          break;
        default:
          break;
      }
      this.props.updateState({ currentStep: stepToUpdate });
    }
    const mediaRecord = {
      id: mediaID,
      file,
      type,
      date: new Date(),
      trainings: [this.props.currentTraining.Id],
    };
    await db.add(STORE_NAMES[1], mediaRecord);
    await this.loadMedia();
  };

  deleteFromDB = async (id) => {
    const db = await initDB();
    const currentStep = this.props.currentStep;
    if(currentStep){
      if(currentStep.ContentImagePath === id){
        currentStep.ContentImagePath = null;
      }
      if(currentStep.ImageFilepath === id){
        currentStep.ImageFilepath = null;
      }
      if(currentStep.VideoFilepath === id){
        currentStep.VideoFilepath = null;
      }
    }
    this.props.updateState({currentStep: currentStep});
    await db.delete(STORE_NAMES[1], id);
    await this.loadMedia();
  }

  // Device management
  loadDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videos = devices.filter((d) => d.kind === "videoinput");
      const audios = devices.filter((d) => d.kind === "audioinput");
      this.setState((prev) => ({
        videoDevices: videos,
        audioDevices: audios,
        selectedVideo:
          prev.selectedVideo || (videos[0] && videos[0].deviceId),
        selectedAudio:
          prev.selectedAudio || (audios[0] && audios[0].deviceId),
      }));
    } catch (err) {
      console.error("Error loading devices:", err);
    }
  };

  stopCamera = () => {
    // Stop and clear media recorder if active
    try {
      if (this.mediaRecorderRef && this.mediaRecorderRef.state !== "inactive") {
        this.mediaRecorderRef.stop();
      }
    } catch (err) {
      console.error("Error stopping media recorder:", err);
    } finally {
      this.mediaRecorderRef = null;
    }

    // Stop all tracks on the active stream and clear the video element
    try {
      if (this.videoRef.current && this.videoRef.current.srcObject) {
        this.videoRef.current.srcObject.getTracks().forEach((track) => {
          try {
            track.stop();
          } catch (err) {
            console.warn("Error stopping track:", err);
          }
        });
        this.videoRef.current.srcObject = null;
      }
    } catch (err) {
      console.error("Error stopping camera tracks:", err);
    }

    // Clear references and update state
    this.currentVideoTrackRef = null;
    this.setState({ cameraStarted: false, recording: false, torchOn: false });
  };

  startCamera = async () => {
    try {
      if (this.videoRef.current?.srcObject) {
        this.videoRef.current.srcObject.getTracks().forEach((t) => t.stop());
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: this.state.selectedVideo
          ? { deviceId: { exact: this.state.selectedVideo } }
          : true,
        audio: this.state.selectedAudio
          ? { deviceId: { exact: this.state.selectedAudio } }
          : true,
      });

      this.videoRef.current.srcObject = stream;
      const videoTrack = stream.getVideoTracks()[0];
      this.currentVideoTrackRef = videoTrack;

      const capabilities = videoTrack.getCapabilities
        ? videoTrack.getCapabilities()
        : {};
      this.setState({ torchAvailable: !!capabilities.torch, torchOn: false });

      this.videoRef.current.onloadedmetadata = () => {
        this.setState({ cameraStarted: true });
      };
      this.loadDevices();
    } catch (err) {
      console.error("Error starting camera:", err);
      const {videoDevices, selectedVideo} = this.state;
      if(videoDevices.length > 1){
        const currentIndex = videoDevices.findIndex(d => d.deviceId === selectedVideo);
        let nextIndex = (currentIndex + 1) % videoDevices.length;
        // If only one device, don't loop forever
        if(nextIndex !== currentIndex){
          this.setState({ selectedVideo: videoDevices[nextIndex].deviceId}, () =>{
            this.startCamera();
          });
        }else{
          alert("Unable to access camera.");
        }
      }else{
        alert("Unable to access camera.");
      }
    }
  };

    toggleTorch = async () => {
    const videoTrack = this.currentVideoTrackRef;
    if (!videoTrack) return;

    try {
      await videoTrack.applyConstraints({
        advanced: [{ torch: !this.state.torchOn }],
      });
      this.setState((prev) => ({ torchOn: !prev.torchOn }));
    } catch (err) {
      console.error("Torch control failed:", err);
    }
  };

  // Capture
  captureImage = async () => {
    const canvas = document.createElement("canvas");
    canvas.width = this.videoRef.current.videoWidth;
    canvas.height = this.videoRef.current.videoHeight;
    canvas
      .getContext("2d")
      .drawImage(this.videoRef.current, 0, 0);
    canvas.toBlob(async (blob) => {
      if (blob) await this.saveToDB(blob, "image");
    }, "image/png");
  };

  startRecording = () => {
    this.setState({ recording: true });
    this.chunksRef = [];
    const stream = this.videoRef.current.srcObject;
    this.mediaRecorderRef = new MediaRecorder(stream, { mimeType: "video/webm" });

    this.mediaRecorderRef.ondataavailable = (e) => this.chunksRef.push(e.data);
    this.mediaRecorderRef.onstop = async () => {
      const blob = new Blob(this.chunksRef, { type: "video/webm" });
      await this.saveToDB(blob, "video");
    };

    this.mediaRecorderRef.start();
  };

  stopRecording = () => {
    this.setState({ recording: false });
    this.mediaRecorderRef.stop();
  };

  // handleUpload = async (e) => {
  //   const file = e.target.files[0];
  //   if (file) {
  //     const type = file.type.startsWith("video") ? "video" : "image";
  //     await this.saveToDB(file, type);
  //   }
  // };

  handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    let convertedFile = file;
    let type = file.type.startsWith("video") ? "video" : "image";

    const filename = file.name.toLowerCase();

    try {
      // 🎬 MOV → MP4 conversion
      if (filename.endsWith('.mov')) {
        console.log(`Converting ${filename} to MP4...`);
        this.setState({ convertingFile: true, convertingFileProgress: null, mediaList: []});
        convertedFile = await convertMovToMp4(file, (progress, time) => {
          console.log(`Conversion progress: ${(progress * 100).toFixed(2)}% at ${(time/100000).toFixed(2)}s`);
          this.setState({ convertingFile: true, convertingFileProgress: (progress * 100).toFixed(2) });
        });
        type = "video";
      }
      // 🖼️ HEIC → PNG conversion
      else if (filename.endsWith('.heic') || filename.endsWith('.heif')) {
        console.log(`Converting ${filename} to PNG...`);
        this.setState({ convertingFile: true, convertingFileProgress: null, mediaList: []});
        convertedFile = await convertHeicToPng(file);
        type = "image";
      }
      this.setState({ convertingFile: false, convertingFileProgress: null});

      // 🧠 Save to IndexedDB or wherever you store it
      await this.saveToDB(convertedFile, type);
    } catch (err) {
      console.error(`Error converting file ${filename}:`, err);
    }
  };

  returnToPrevious = () => {
    this.stopCamera();
    if(!this.props.currentStep?.VideoFilepath && this.props.currentStep?.StepType === 4){
      console.log("Returning to previous page (skipping video-model-step since no video was captured)");
      this.props.navigate(-2);
    } else {
      this.props.navigate(-1);  
    }
  };

  render() {
    const {
      mediaList,
      recording,
      videoDevices,
      audioDevices,
      selectedVideo,
      selectedAudio,
      torchAvailable,
      torchOn,
      cameraStarted,
      osLabel,
      mobile
    } = this.state;
    console.log("Rendering MediaCapture", this.state);
    if(!this.props.currentTraining){
      return (<div><p>No training selected.</p></div>);
    }
    let mediaTitle;
    switch(this.props.currentStep?.StepType){
      // case 1:
      //   mediaTitle = ;
      //   break;
      case 2:
        mediaTitle = this.props.currentStep?.InfoSlideType === 2 ? `${mediaList > 0 ? 'Update' : 'Add'} an image for Conclusion Step` : `${mediaList > 0 ? 'Update' : 'Add'} an image for Introduction Step`;
        break;
      case 3:
        mediaTitle = `${mediaList > 0 ? 'Update' : 'Add'} an image demonstrating ${this.props.currentStep.Title}`;
        break;
      case 4:
        mediaTitle = `${mediaList > 0 ? 'Update' : 'Add'} a short video to demonstrate ${this.props.currentStep.Title}`;
        break;
      default:
        mediaTitle = "Media Capture";
        break;
    }
    return (
      <div className="p-4 space-y-4">
        <h2 className="text-xl font-bold">{mediaTitle}</h2>
        {this.state.convertingFile && 
          <Alert color="info">
            {/* Text */}
            <span>
              File conversion in progress
              {this.state.convertingFileProgress
                ? ` ${this.state.convertingFileProgress}% Complete`
                : '... Please wait'}
            </span>
              </Alert>
        }
        {mobile ? <FormGroup
            className="file-input-container d-flex align-items-center mt-2"
            style={{ gap: '10px' }}
          >          
            {/* File Input */}
            <Input
              style={{ display: 'none' }}
              id="content-input"
              size={'lg'}
              type="file"
              accept={
                this.state.fileType === "video" ? "video/*" : "image/*"
              }
              onChange={this.handleUpload}
              className="mt-2"
            />
            <Button
              onClick={() => document.getElementById('content-input').click()}
              color={(mediaList && mediaList.length > 0) ? "secondary" : "primary"}
              size="lg"
              style={{ flex: '1', minWidth: '120px', margin: '5px'}}
            >
              {osLabel}
            </Button>
          </FormGroup> :
          <FormGroup
            className="file-input-container d-flex align-items-center mt-2"
            style={{ gap: '10px' }}
          >          
            {/* File Input */}
            <Label className="block mt-3 text-sm font-large">{osLabel}: </Label>
            <Input
              style={{ margin: '5px', flex: '1'}}
              size={'lg'}
              type="file"
              accept={
                this.state.fileType === "video" ? "video/*" : "image/*"
              }
              onChange={this.handleUpload}
              className="mt-2"
            />
          </FormGroup>
        }

        {/* Start Camera Button */}

        <ButtonGroup className='instruct-control-bar' size='lg'>
            {!mobile && !cameraStarted && (
              <Button
                onClick={this.startCamera}
                color={(mediaList && mediaList.length > 0) ? "secondary" : "primary"}
              >
                Start Camera Preview
              </Button>
            )}

            {!mobile && cameraStarted && (
              <Button 
                onClick={this.stopCamera} 
                disabled={recording}
                color={(mediaList && mediaList.length > 0) ? "secondary" : "primary"}
              >
                Stop Camera Preview
              </Button>
            )}

            <Button
              onClick={this.returnToPrevious}
              color={(mediaList && mediaList.length > 0) ? "primary" : "secondary"}
            >
              Return
            </Button>
          </ButtonGroup>
        {!mobile && <>
          <div className="space-y-2">
            {videoDevices.length > 1 && (
              <div className="select-device-controls">
                <label className="block text-sm font-medium">Select Camera:</label>
                <select
                  value={selectedVideo || ""}
                  onChange={(e) => this.setState({ selectedVideo: e.target.value })}
                  className="border p-1 rounded w-full"
                  disabled={recording}
                >
                  {videoDevices.map((d) => (
                    <option key={d.deviceId} value={d.deviceId}>
                      {d.label || `Camera ${d.deviceId}`}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {!mobile && <video
            ref={this.videoRef}
            autoPlay
            playsInline
            muted
            className={`video-preview border rounded w-full max-w-md transition-all ${
              cameraStarted ? "opacity-100" : "opacity-0 h-0"
            }`}
          />}

          {cameraStarted && <div className="space-x-2 media-capture-controls">
            {this.state.fileType === "image" && (
              <Button
                onClick={this.captureImage}
                size="lg"
                className="bg-green-600 text-white rounded px-3 py-1 primary"
              >
                Capture Image
              </Button>
            )}

            {this.state.fileType === "video" &&
              (!recording ? (
                <Button
                  onClick={this.startRecording}
                  size="lg"
                  className="bg-red-600 text-white rounded px-3 py-1 primary"
                  disabled={!cameraStarted}
                >
                  Start Recording
                </Button>
              ) : (
                <Button
                  onClick={this.stopRecording}
                  size="lg"
                  className="bg-gray-600 text-white rounded px-3 py-1 primary"
                >
                  Stop Recording
                </Button>
              ))}

            {torchAvailable && (
              <Button
                onClick={this.toggleTorch}
                size="lg"
                className={`rounded text-white px-3 py-1 ${
                  torchOn ? "bg-yellow-600" : "bg-gray-700"
                }`}
              >
                {torchOn ? "Torch On" : "Torch Off"}
              </Button>
            )}
          </div>}
        </>}

        {/* Saved Media */}
        {(mediaList && mediaList.length > 0) && <div className="grid grid-cols-2 gap-4 current-media">
          {mediaList.map((item) => (
            <div
              key={item.id}
              className="border p-2 rounded flex flex-col items-center max-w-[500px] mx-auto"
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
            >              
              {item.type === "image" ? (
                <img
                  src={URL.createObjectURL(item.file)}
                  alt="Captured"
                  className="w-full rounded"
                  style={{
                    maxWidth: '500px',
                    maxHeight: '500px',
                    objectFit: 'contain'
                  }}
                />
              ) : (
                <video
                  controls
                  src={URL.createObjectURL(item.file)}
                  className="w-full rounded"
                  style={{
                    maxWidth: '500px',
                    maxHeight: '500px',
                    objectFit: 'contain'
                  }}
                />
              )}

              <Button
                size="lg"
                onClick={() => this.deleteFromDB(item.id)}
                className="mt-2 bg-red-500 text-xs px-3 py-1 rounded"
                color="danger"
              >
                Delete
              </Button>
            </div>
          ))}
        </div>}
      </div>
    );
  }
}

export default withNavigation(MediaCaptureWithTour);
